import { common } from '@/features/i18n/common';
import { t } from '@/features/i18n/utils';
import { ProjectsList } from '@/features/projects/components/projects-list';
import { useAuth } from '@/features/auth';
import { Breadcrumb, BreadcrumbPage } from '@/ui/components/breadcrumb';
import { Header, HeaderOrganizationMenu, HeaderUserMenu } from '@/ui/components/header';
import { toastManager } from '@/ui/components/toast';
import { Building06Icon } from '@hugeicons-pro/core-stroke-rounded';
import { Icon } from '@/ui/components/icon';
import { createFileRoute } from '@tanstack/react-router';
import { usePostHog } from 'posthog-js/react';
import { useEffect } from 'react';
import { z } from 'zod';

export const Route = createFileRoute('/_authed/_organization/')({
  component: RouteComponent,
  validateSearch: z.object({
    inviteAccepted: z.boolean().optional(),
    organizationName: z.string().optional(),
    showArchived: z.boolean().optional(),
  }),
});

function RouteComponent() {
  const auth = useAuth();
  const search = Route.useSearch();
  const inviteAccepted = search?.inviteAccepted;
  const organizationName = search?.organizationName;
  const posthog = usePostHog();

  useEffect(() => {
    if (auth.user.id && import.meta.env.VITE_POSTHOG_KEY) {
      posthog.identify(auth.user.id, {
        email: auth.user.email,
        name: `${auth.user.firstName} ${auth.user.lastName}`,
      });

      if (auth.user.organizationId && auth.user.organizationName) {
        posthog.group('organization', auth.user.organizationId, {
          name: auth.user.organizationName,
        });
      }
    }
  }, [auth.user, posthog]);

  useEffect(() => {
    if (inviteAccepted && organizationName) {
      const id = toastManager.add({
        type: 'success',
        title: t({ en: 'Successfully joined', nb: 'Vellykket' }),
        description: t({
          en: (
            <>
              You are now a member of <b>{organizationName}</b> organisation
            </>
          ),
          nb: (
            <>
              Du er nå medlem av organisasjonen <b>{organizationName}</b>
            </>
          ),
        }),
        timeout: Infinity,
        actionProps: {
          children: t(common.dismiss),
          onClick: () => toastManager.close(id),
        },
      });
    }
  }, [inviteAccepted, organizationName]);

  return (
    <>
      <Header>
        <HeaderOrganizationMenu />
        <Breadcrumb>
          <BreadcrumbPage>
            <Icon icon={Building06Icon} />
            {t({ en: 'Projects', nb: 'Prosjekter' })}
          </BreadcrumbPage>
        </Breadcrumb>
        <HeaderUserMenu />
      </Header>
      <ProjectsList />
    </>
  );
}
